import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Images, Loader2, Lock, Trash2, Upload } from "lucide-react";
import { FaPaw } from "react-icons/fa";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { ADMIN_EMAIL } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FadeUp, MaskLines } from "@/components/Reveal";
import CropDialog from "@/components/CropDialog";
import Lightbox from "@/components/Lightbox";

export default function Gallery() {
  const { user, isAdmin, login } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchImages = useCallback(async () => {
    try {
      const { data } = await api.get("/gallery");
      setImages(data);
    } catch {
      toast.error("Couldn't load the den. Try refreshing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      await login(ADMIN_EMAIL, password);
      toast.success("Welcome back. The den is yours.");
      setLoginOpen(false);
      setPassword("");
    } catch (err) {
      toast.error(formatApiError(err, "Login failed"));
    } finally {
      setLoggingIn(false);
    }
  };

  const handleFileSelected = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("That's not an image. Husko checked.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Too heavy — 15 MB max per image.");
      return;
    }
    setCropFile({ src: URL.createObjectURL(file), name: file.name });
  };

  const handleCropped = async (blob, caption) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `husko-${Date.now()}.jpg`, { type: "image/jpeg" }));
      if (caption) fd.append("caption", caption);
      const { data } = await api.post("/gallery", fd);
      setImages((prev) => [data, ...prev]);
      toast.success("Uploaded! Looking fluffy.");
      setCropFile(null);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
      if (err?.response?.status === 401) setLoginOpen(true);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/gallery/${deleteTarget.id}`);
      setImages((prev) => prev.filter((img) => img.id !== deleteTarget.id));
      toast("Sent to the doghouse (deleted).");
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed"));
    } finally {
      setDeleteTarget(null);
    }
  };

  const navLightbox = (dir) => {
    setLightbox((i) => (i == null ? i : (i + dir + images.length) % images.length));
  };

  return (
    <div className="pt-16">
      <section
        className="hero-glow relative overflow-hidden"
        onDragOver={(e) => {
          e.preventDefault();
          if (isAdmin) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (isAdmin) handleFileSelected(e.dataTransfer.files?.[0]);
        }}
      >
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-husko-magenta">
            {images.length} {images.length === 1 ? "snap" : "snaps"} in the den
          </p>
          <h1 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            <MaskLines baseDelay={0.1} lines={["The", <span key="g" className="text-gradient-husky">gallery</span>]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7 }}
            className="mt-6 max-w-lg text-base text-slate-300 sm:text-lg"
          >
            Zoomies, photoshoots and general fluff. Click any pic to view it big.
          </motion.p>

          {isAdmin ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className={`mt-8 flex flex-wrap items-center gap-4 rounded-2xl border p-5 transition-colors ${
                dragOver ? "border-husko-cyan bg-husko-cyan/10" : "border-white/10 bg-den"
              }`}
              data-testid="gallery-admin-toolbar"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  handleFileSelected(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <Button
                data-testid="upload-image-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="cursor-pointer gap-2 rounded-full bg-gradient-to-r from-husko-cyan to-husko-purple font-display font-extrabold uppercase text-void hover:opacity-90"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                Upload image
              </Button>
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                <FaPaw size={11} className="text-husko-cyan" />
                Drop an image anywhere here — crop it, caption it, done
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="mt-8"
            >
              <button
                data-testid="admin-login-button"
                disabled={user === null}
                onClick={() => setLoginOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-slate-300 transition-colors hover:border-husko-cyan hover:text-husko-cyan disabled:opacity-40"
              >
                <Lock size={14} /> Admin login
              </button>
            </motion.div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32" data-testid="gallery-grid-section">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={32} className="animate-spin text-husko-cyan" />
          </div>
        ) : images.length === 0 ? (
          <FadeUp>
            <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-white/15 bg-den/50 px-6 py-24 text-center" data-testid="gallery-empty-state">
              <Images size={40} className="text-husko-cyan/60" />
              <p className="mt-5 max-w-sm text-slate-400">
                The den is empty — Husko hasn't dropped any pics yet. Admins can upload
                the first fluffy one.
              </p>
            </div>
          </FadeUp>
        ) : (
          <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
            {images.map((img, i) => (
              <motion.figure
                key={img.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                className="group relative mb-5 break-inside-avoid overflow-hidden rounded-2xl border border-white/10 bg-den"
                data-testid={`gallery-image-card-${i}`}
              >
                <button
                  className="block w-full cursor-zoom-in"
                  onClick={() => setLightbox(i)}
                  data-testid={`gallery-image-open-${i}`}
                  aria-label={img.caption || "Open image"}
                >
                  <img
                    src={fileUrl(img.storage_path)}
                    alt={img.caption || "Husko gallery snap"}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                </button>
                {img.caption && (
                  <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-void/90 via-void/50 to-transparent px-4 pb-3 pt-10 text-sm font-medium text-slate-100 opacity-0 transition-opacity group-hover:opacity-100">
                    {img.caption}
                  </figcaption>
                )}
                {isAdmin && (
                  <button
                    data-testid={`delete-image-button-${i}`}
                    className="absolute right-3 top-3 rounded-full bg-void/80 p-2 text-slate-300 opacity-0 backdrop-blur transition-all hover:bg-husko-magenta hover:text-void group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(img);
                    }}
                    aria-label="Delete image"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </motion.figure>
            ))}
          </div>
        )}
      </section>

      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="max-w-sm border-white/10 bg-den" data-testid="admin-login-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase tracking-tight">
              Who goes there?
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              data-testid="admin-password-input"
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="border-white/15 bg-void"
            />
            <Button
              data-testid="admin-login-submit-button"
              type="submit"
              disabled={loggingIn || !password}
              className="w-full cursor-pointer gap-2 rounded-full bg-gradient-to-r from-husko-cyan to-husko-purple font-display font-extrabold uppercase text-void hover:opacity-90"
            >
              {loggingIn ? <Loader2 size={16} className="animate-spin" /> : <FaPaw size={14} />}
              Enter the den
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-den">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-extrabold">
              Delete this snap?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.caption || "This image"} will be removed from the gallery. No take-backs (well, you can re-upload).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="cursor-pointer">
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={handleDelete}
              className="cursor-pointer bg-husko-magenta text-void hover:bg-husko-magenta/80"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AnimatePresence>
        {lightbox != null && (
          <Lightbox images={images} index={lightbox} onClose={() => setLightbox(null)} onNav={navLightbox} />
        )}
      </AnimatePresence>

      {cropFile && (
        <CropDialog
          cropFile={cropFile}
          uploading={uploading}
          onCancel={() => setCropFile(null)}
          onConfirm={handleCropped}
        />
      )}
    </div>
  );
}
